import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { DataTable } from '../components/DataTable';
import type { Column } from '../components/DataTable';
import { formatMinutes } from '../lib/types';
import { formatDate, monthStartValue, todayValue } from '../lib/dates';
import { TimeLogExportModal } from './TimeLogExportModal';
import { useAsk } from '../components/Ask';

interface Cut {
  id: string;
  name: string;
  minutes: number;
  billableMinutes: number;
  writtenOffMinutes: number;
  approvedMinutes: number;
  invoicedMinutes: number;
  amount: number | null;
}

interface Entry {
  id: string;
  date: string;
  person: string;
  project: string;
  projectCode: string;
  projectBillable: boolean;
  task: { key: string; title: string } | null;
  description: string;
  minutes: number;
  billable: boolean;
  status: string;
  amount: number | null;
  /** When this hour was invoiced, and on what. Null for anybody who may not bill. */
  invoicedAt: string | null;
  invoiceRef: string;
}

interface Log {
  company: { id: string; name: string };
  from: string;
  to: string;
  totals: {
    minutes: number;
    billableMinutes: number;
    writtenOffMinutes: number;
    approvedMinutes: number;
    awaitingApprovalMinutes: number;
    amount: number | null;
    invoicedMinutes: number;
    invoicedAmount: number | null;
    toInvoiceMinutes: number;
  };
  byPerson: Cut[];
  byProject: Cut[];
  entries: Entry[];
  costed: boolean;
  /** Whether this reader may mark time billed, so the page offers it or does not. */
  mayBill: boolean;
}

/** The tabs over "Every hour booked" — which hours the list shows. */
type Show = 'all' | 'billable' | 'ready' | 'awaiting' | 'invoiced' | 'written_off';
const SHOWS: Array<{ key: Show; label: string; hint: string }> = [
  { key: 'ready', label: 'Ready to invoice', hint: 'Billable, approved and not yet invoiced' },
  { key: 'awaiting', label: 'Awaiting approval', hint: 'Billable but nobody has approved it yet' },
  { key: 'invoiced', label: 'Invoiced', hint: 'Already marked as sent on an invoice' },
  { key: 'written_off', label: 'Written off', hint: 'Not billable — the project or the task says so' },
  { key: 'billable', label: 'All billable', hint: 'Every billable hour, whatever its state' },
  { key: 'all', label: 'Everything', hint: 'Every hour booked' },
];

/**
 * THIS MONTH SO FAR — the first of the month up to today.
 *
 * It used to open on the whole of LAST month, on the argument that whoever is invoicing is
 * usually looking backwards. Most people opening this report are not invoicing: they want to
 * know where the hours have gone lately, and a report that opens on a period which has already
 * closed makes them change the dates before it says anything about now. The month end is a
 * handful of days a month; the rest of the time this is the question being asked.
 *
 * Both ends still move, so a longer look back is two clicks away.
 */
function monthToDate(): { from: string; to: string } {
  return { from: monthStartValue(), to: todayValue() };
}

/**
 * Every hour booked for a company, who spent it, and what is chargeable.
 *
 * TWO READERS, ONE PAGE. The team reads it to size the next job — an estimate is built from who
 * did what and how long it took them, and a project total cannot tell you that. Finance reads
 * the export to raise an invoice.
 *
 * The money only appears for an administrator, on the same rule as the rate cards themselves.
 * Everybody else gets the hours, which is most of the use of it.
 */
export function TimeLogReport({ companyId }: { companyId: string }) {
  const [range, setRange] = useState(monthToDate);
  const [log, setLog] = useState<Log | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [billing, setBilling] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  /* The entry currently being taken back, so only that one row's button says so. */
  const [undoing, setUndoing] = useState<string | null>(null);
  /** Which hours the list shows — null until chosen, which means the default for this reader. */
  const [chosenShow, setChosenShow] = useState<Show | null>(null);
  const [exporting, setExporting] = useState(false);
  const ask = useAsk();

  /**
   * MARK WHAT WAS TICKED AS SENT.
   *
   * THE FIRST VERSION TOOK A DATE RANGE AND SHOWED NOTHING. The page has two aggregate cuts and
   * no list of entries, so "mark this window" acted on a set nobody could see, check beforehand
   * or verify after — the only way to find out what it had done was to open the export. That is
   * not a thing to ask somebody to press.
   *
   * An invoice is rarely exactly a window either: a project gets held back, one person's hours
   * wait on a query, a month goes out as two invoices. So the entries are on screen and the
   * ticks decide. "All of it" is still one click — see the header checkbox — because month end
   * usually IS all of it, and ticking two hundred rows would be its own reason not to bother.
   *
   * The API checks billable, approved and not-already-sent again when it saves, so a list that
   * has gone stale in a tab cannot mark something it should not.
   */
  async function markBilled() {
    if (!log || billing) return;

    const reference = await ask.prompt({
      title: `Mark ${picked.size} ${picked.size === 1 ? 'entry' : 'entries'} as invoiced?`,
      body:
        'They are recorded as sent and stop counting as work to invoice. Anything that has since ' +
        'been invoiced, un-approved or written off is skipped — the rule is checked again when ' +
        'this is saved, not when the list was drawn.',
      label: 'Invoice number (optional)',
      placeholder: 'INV-1041',
      confirmLabel: 'Mark as invoiced',
      /*
        `minLength` defaults to 1, which would have made a field labelled "optional" impossible
        to leave empty. A run with no reference is still a record that the hours went out.
      */
      minLength: 0,
    });
    if (reference === null) return;

    setBilling(true);
    setError(null);
    setNotice(null);
    try {
      const done = await api.post<{ marked: number }>(`/companies/${companyId}/time-log/billed`, {
        from: range.from,
        to: range.to,
        ids: [...picked],
        ...(reference.trim() ? { reference: reference.trim() } : {}),
      });
      setPicked(new Set());
      await load();
      /*
        Said on the page rather than in a dialog. "Nothing was marked" is the answer somebody
        wondering whether they already ran it needs to be able to re-read, and a modal that has
        to be dismissed to see the numbers it changed is a modal in the way of its own result.
      */
      setNotice(
        done.marked === 0
          ? 'Nothing left to mark — everything billable and approved in this window was already invoiced.'
          : `${done.marked} ${done.marked === 1 ? 'entry' : 'entries'} marked as invoiced. They no longer count as work to invoice, and the export leaves them out.`,
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not mark the time as invoiced');
    } finally {
      setBilling(false);
    }
  }

  /**
   * TAKING AN HOUR BACK OFF ITS INVOICE — and, when it is one of several, the whole invoice.
   *
   * The button is on the row because that is where somebody notices: reading down the month and
   * seeing a line marked that should not have been. But the mistake is not always one line. "We
   * never sent INV-1041" is just as common, and clicking forty rows to say so is a punishment
   * for having noticed.
   *
   * SO THE DIALOG ASKS, and only when there is something to ask: an invoice with one hour in
   * this window gets a plain confirm, because offering "all 1 of them" is a choice that isn't
   * one. `ask.choose` rather than a second button on the page — the decision belongs at the
   * moment of deciding, not as furniture somebody reads past every time they open the report.
   *
   * DISMISSAL DOES NOTHING, which is the whole reason `choose` exists here. A three-way
   * decision squeezed into OK/Cancel means Escape silently picks whichever one Cancel meant,
   * and this is a line changing hands between "charged for" and "still to charge for".
   */
  async function undoEntry(row: Entry) {
    if (!log || undoing) return;

    const named = row.invoiceRef || 'the run with no invoice number';
    /* Its companions on the same invoice, inside this window — which is all the undo can reach. */
    const kin = log.entries.filter((e) => e.invoicedAt && e.invoiceRef === row.invoiceRef);
    const whole = kin.reduce((sum, e) => sum + e.minutes, 0);

    let scope: 'row' | 'run' | null = null;
    if (kin.length > 1) {
      const answer = await ask.choose({
        title: `Take back ${formatMinutes(row.minutes)} on ${formatDate(row.date)}?`,
        body:
          `It went out on ${named}, which has ${kin.length} entries in this window — ` +
          `${formatMinutes(whole)} in all. Whichever you choose, the hours themselves are untouched: ` +
          'this only undoes the record that they went out.',
        options: [
          { value: 'row', label: 'Just this hour', danger: true },
          { value: 'run', label: `All ${kin.length} on ${named}`, danger: true },
        ],
      });
      if (answer !== 'row' && answer !== 'run') return;
      scope = answer;
    } else {
      const sure = await ask.confirm({
        title: `Take this hour back off ${named}?`,
        body:
          `${formatMinutes(row.minutes)} on ${formatDate(row.date)} stops counting as invoiced and goes ` +
          'back to work to invoice. The hour itself is untouched — this only undoes the record that it ' +
          'went out.',
        confirmLabel: 'Take it back',
        danger: true,
      });
      if (!sure) return;
      scope = 'row';
    }

    setUndoing(row.id);
    setError(null);
    setNotice(null);
    try {
      /**
       * THE WHOLE RUN GOES BY REFERENCE, NOT BY THE IDS ON SCREEN.
       *
       * Sending the ids would undo only the lines this window happens to show, which is a
       * different thing from undoing the invoice and would leave it half taken back with no
       * sign of it. The reference-and-window form is the one that means "that invoice".
       */
      const done = await api.del<{ unmarked: number }>(`/companies/${companyId}/time-log/billed`, {
        from: range.from,
        to: range.to,
        ...(scope === 'row' ? { ids: [row.id] } : { reference: row.invoiceRef }),
      });
      await load();
      setNotice(
        done.unmarked === 0
          ? 'Nothing was taken back — it had already been taken off, or is no longer in this window.'
          : scope === 'row'
            ? `${formatMinutes(row.minutes)} taken back off ${named}. It counts as work to invoice again.`
            : `${done.unmarked} ${done.unmarked === 1 ? 'entry' : 'entries'} taken back off ${named}. They count as work to invoice again.`,
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not take that back');
    } finally {
      setUndoing(null);
    }
  }

  const load = useCallback(async () => {
    setError(null);
    try {
      setLog(await api.get<Log>(`/companies/${companyId}/time-log?from=${range.from}&to=${range.to}`));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not build the time log');
      setLog(null);
    }
  }, [companyId, range.from, range.to]);

  useEffect(() => {
    void load();
  }, [load]);

  /* A tick means a row, and the rows change with the window. Carrying it over would be a lie. */
  useEffect(() => {
    setPicked(new Set());
    setNotice(null);
  }, [range.from, range.to, companyId]);

  const cuts = (heading: string): Column<Cut>[] => [
    { key: 'name', header: heading, value: (row) => row.name, filter: 'text', render: (row) => row.name },
    {
      key: 'minutes',
      header: 'Booked',
      value: (row) => row.minutes,
      filter: 'none',
      render: (row) => <span className="mono">{formatMinutes(row.minutes)}</span>,
    },
    {
      key: 'billable',
      header: 'Billable',
      value: (row) => row.billableMinutes,
      filter: 'none',
      render: (row) => <span className="mono">{formatMinutes(row.billableMinutes)}</span>,
    },
    /* Only once something in this window has been billed — an all-dashes column is noise. */
    ...(log && log.totals.invoicedMinutes > 0
      ? [
          {
            key: 'invoiced',
            header: 'Invoiced',
            value: (row: Cut) => row.invoicedMinutes,
            filter: 'none' as const,
            render: (row: Cut) =>
              row.invoicedMinutes > 0 ? (
                <span className="mono dim">{formatMinutes(row.invoicedMinutes)}</span>
              ) : (
                <span className="dim">—</span>
              ),
          },
        ]
      : []),
    {
      key: 'written',
      header: 'Written off',
      value: (row) => row.writtenOffMinutes,
      filter: 'none',
      render: (row) =>
        row.writtenOffMinutes > 0 ? (
          <span className="mono dim">{formatMinutes(row.writtenOffMinutes)}</span>
        ) : (
          <span className="dim">—</span>
        ),
    },
    ...(log?.costed
      ? [
          {
            key: 'amount',
            header: 'To invoice',
            value: (row: Cut) => row.amount ?? 0,
            filter: 'none' as const,
            render: (row: Cut) => (
              <span className="mono">{row.amount === null ? '—' : row.amount.toFixed(2)}</span>
            ),
          },
        ]
      : []),
  ];

  /**
   * WHETHER THIS ROW CAN GO ON AN INVOICE, and if not, why not.
   *
   * Said per row rather than hidden, because "it is not in the list" and "it is in the list and
   * cannot be ticked, because nobody has approved it" are different answers and the second is
   * the one that tells somebody what to do next.
   */
  const billable = (row: Entry): { can: boolean; why: string } => {
    if (row.invoicedAt) return { can: false, why: row.invoiceRef ? `Invoiced · ${row.invoiceRef}` : 'Invoiced' };
    if (!row.billable) return { can: false, why: 'Written off' };
    if (row.status !== 'approved') return { can: false, why: 'Not approved yet' };
    return { can: true, why: '' };
  };

  /**
   * WHICH HOURS TO LOOK AT. Finance opens this to invoice, so for whoever may bill it starts on
   * the hours ready to go on an invoice; everybody else starts on all of them. Each tab says how
   * many it holds, so "is there anything left to invoice" is answered before choosing.
   */
  const show: Show = chosenShow ?? (log?.mayBill ? 'ready' : 'all');
  const kindOf = (row: Entry): Exclude<Show, 'all' | 'billable'> =>
    row.invoicedAt ? 'invoiced' : !row.billable ? 'written_off' : row.status !== 'approved' ? 'awaiting' : 'ready';
  const inView = (row: Entry, view: Show) => view === 'all' || (view === 'billable' ? row.billable : kindOf(row) === view);
  const shownEntries = (log?.entries ?? []).filter((row) => inView(row, show));
  const selectable = shownEntries.filter((row) => billable(row).can);
  const allPicked = selectable.length > 0 && selectable.every((row) => picked.has(row.id));

  const entryColumns: Column<Entry>[] = [
    ...(log?.mayBill
      ? [
          {
            key: 'pick',
            /* The header is the select-all, which is the month-end case in one click. */
            header: (
              <input
                type="checkbox"
                aria-label={allPicked ? 'Clear the selection' : 'Select everything that can be invoiced'}
                checked={allPicked}
                disabled={selectable.length === 0}
                onChange={() => setPicked(allPicked ? new Set() : new Set(selectable.map((row) => row.id)))}
              />
            ) as unknown as string,
            value: (row: Entry) => (picked.has(row.id) ? 1 : 0),
            filter: 'none' as const,
            render: (row: Entry) => {
              const state = billable(row);
              return (
                <input
                  type="checkbox"
                  aria-label={`Include ${row.date} ${row.person}`}
                  checked={picked.has(row.id)}
                  disabled={!state.can}
                  title={state.can ? undefined : state.why}
                  onChange={() =>
                    setPicked((current) => {
                      const next = new Set(current);
                      if (next.has(row.id)) next.delete(row.id);
                      else next.add(row.id);
                      return next;
                    })
                  }
                />
              );
            },
          },
        ]
      : []),
    { key: 'date', header: 'Date', value: (row) => row.date, filter: 'text', render: (row) => formatDate(row.date) },
    { key: 'person', header: 'Person', value: (row) => row.person, filter: 'text', render: (row) => row.person },
    { key: 'project', header: 'Project', value: (row) => row.project, filter: 'text', render: (row) => row.project },
    {
      key: 'task',
      header: 'Task',
      value: (row) => row.task?.key ?? '',
      filter: 'text',
      render: (row) => (row.task ? <span className="mono">{row.task.key}</span> : <span className="dim">—</span>),
    },
    {
      key: 'description',
      header: 'What was done',
      value: (row) => row.description,
      filter: 'text',
      render: (row) => row.description || <span className="dim">—</span>,
    },
    {
      key: 'minutes',
      header: 'Hours',
      value: (row) => row.minutes,
      filter: 'none',
      render: (row) => <span className="mono">{formatMinutes(row.minutes)}</span>,
    },
    {
      key: 'state',
      header: 'Invoice',
      value: (row) => billable(row).why || 'Ready',
      filter: 'text',
      render: (row) => {
        const state = billable(row);
        if (row.invoicedAt) {
          return (
            <span className="dim" title={`Invoiced ${formatDate(String(row.invoicedAt).slice(0, 10))}`}>
              {state.why}
            </span>
          );
        }
        return state.can ? <span className="badge">Ready</span> : <span className="dim">{state.why}</span>;
      },
    },
    ...(log?.costed
      ? [
          {
            key: 'amount',
            header: 'Amount',
            value: (row: Entry) => row.amount ?? 0,
            filter: 'none' as const,
            render: (row: Entry) => (
              <span className="mono">{row.amount === null ? '—' : row.amount.toFixed(2)}</span>
            ),
          },
        ]
      : []),
    /**
     * TAKING ONE LINE BACK, on the line itself.
     *
     * Marking is one click over a whole month, so getting it wrong is one click too — the wrong
     * window, the wrong rows, an invoice that was prepared and never went. The way back belongs
     * where the mistake is visible: beside the hour it was made on, next to what it was worth.
     *
     * ONLY FOR WHOEVER MAY BILL, which is the billing hat and an administrator and nobody else.
     * The column is not there at all otherwise — the server refuses it too, but a button that
     * exists only to answer no is worse than no button.
     *
     * Blank on a row that was never invoiced, rather than a disabled button on every line. Most
     * of the table is uninvoiced most of the time, and a column of dead controls reads as a
     * column of things that are broken.
     */
    ...(log?.mayBill
      ? [
          {
            key: 'undo',
            header: '',
            value: (row: Entry) => (row.invoicedAt ? 1 : 0),
            filter: 'none' as const,
            render: (row: Entry) =>
              row.invoicedAt ? (
                <button
                  type="button"
                  className="btn btn-quiet btn-sm"
                  disabled={undoing !== null}
                  title={
                    row.invoiceRef
                      ? `Take this hour back off ${row.invoiceRef}, so it counts as work to invoice again`
                      : 'Take this hour back, so it counts as work to invoice again'
                  }
                  onClick={() => void undoEntry(row)}
                >
                  {undoing === row.id ? 'Taking back…' : 'Take back'}
                </button>
              ) : null,
          },
        ]
      : []),
  ];

  if (error) return <div className="callout">{error}</div>;
  if (!log) return <div className="empty">Building the time log…</div>;

  return (
    <>
      <div className="row mb-2" style={{ gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <label className="field" style={{ marginBottom: 0 }}>
          <span className="field-label">From</span>
          <input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} />
        </label>
        <label className="field" style={{ marginBottom: 0 }}>
          <span className="field-label">To</span>
          <input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} />
        </label>

        {/*
          ASKS FIRST. It downloaded every hour and every column straight away, and finance
          deleted most of it in Excel; the pop-up asks which hours, projects and columns, and
          starts on whichever tab the list below is showing.
        */}
        {log.costed && (
          <button type="button" className="btn" style={{ marginLeft: 'auto' }} onClick={() => setExporting(true)}>
            Export for finance
          </button>
        )}
      </div>

      {exporting && (
        <TimeLogExportModal
          companyId={companyId}
          companyName={log.company.name}
          from={range.from}
          to={range.to}
          rows={log.entries}
          initialShow={show === 'written_off' ? 'not_billable' : show}
          onClose={() => setExporting(false)}
        />
      )}

      <div className="stat-row mb-2">
        <div className="stat">
          <div className="stat-label">Booked</div>
          <div className="stat-value">{formatMinutes(log.totals.minutes)}</div>
        </div>
        <div className="stat">
          <div className="stat-label">Billable</div>
          <div className="stat-value">{formatMinutes(log.totals.billableMinutes)}</div>
        </div>
        <div className="stat">
          <div className="stat-label">Written off</div>
          <div className="stat-value">{formatMinutes(log.totals.writtenOffMinutes)}</div>
        </div>
        {/*
          The number somebody chasing an invoice actually needs: chargeable work that nobody has
          signed off, and which therefore cannot be billed yet.
        */}
        <div className="stat">
          <div className="stat-label">Awaiting approval</div>
          <div className="stat-value" style={log.totals.awaitingApprovalMinutes > 0 ? { color: 'var(--accent)' } : undefined}>
            {formatMinutes(log.totals.awaitingApprovalMinutes)}
          </div>
        </div>
        {log.costed && (
          <div className="stat">
            <div className="stat-label">To invoice</div>
            <div className="stat-value">{(log.totals.amount ?? 0).toFixed(2)}</div>
            {/*
              It used to read "billable and approved only" and went on saying the same figure
              after the invoice had gone out. Narrowed by what has been marked, so the tile
              counts down as work is billed rather than standing still.
            */}
            <div className="stat-label" style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
              {formatMinutes(log.totals.toInvoiceMinutes)} · not yet sent
            </div>
          </div>
        )}
        {log.costed && log.totals.invoicedMinutes > 0 && (
          <div className="stat">
            <div className="stat-label">Invoiced</div>
            <div className="stat-value dim">{(log.totals.invoicedAmount ?? 0).toFixed(2)}</div>
            <div className="stat-label" style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
              {formatMinutes(log.totals.invoicedMinutes)} · already sent
            </div>
          </div>
        )}
      </div>

      {log.entries.length === 0 ? (
        <div className="card">
          <div className="empty">
            <h3>No time booked</h3>
            <p>
              Nothing was booked for this company between {formatDate(log.from)} and {formatDate(log.to)}.
            </p>
          </div>
        </div>
      ) : (
        <div className="stack">
          <div className="card">
            <div className="card-head">
              <h3>Who spent it</h3>
              <span className="dim" style={{ fontSize: 12 }}>
                What the next estimate is built from
              </span>
            </div>
            <DataTable tableKey="time-log-people" rows={log.byPerson} columns={cuts('Person')} />
          </div>

          <div className="card">
            <div className="card-head">
              <h3>Where it went</h3>
            </div>
            <DataTable tableKey="time-log-projects" rows={log.byProject} columns={cuts('Project')} />
          </div>

          {/*
            EVERY HOUR, LAST. The two cuts above answer "where did the month go", which is what
            most people open this for; this is the detail you drop into — to check a line, or to
            tick what went on an invoice. It is still the thing the button acts on, which is why
            it is on the page at all: marking a set nobody can see is not something to ask anybody
            to press.
          */}
          <div className="card">
            <div className="card-head">
              <h3>Every hour booked</h3>
              {/*
                MARKING LIVES WITH THE TICKS. It sat in the toolbar at the top of the page, a long
                scroll away from the rows it acts on — tick at the bottom, scroll up, press. It is
                here now, beside the list it marks, and counts what is ticked.
              */}
              {log.mayBill ? (
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={billing || picked.size === 0}
                  title={picked.size === 0 ? 'Tick the hours that went on the invoice' : undefined}
                  onClick={() => void markBilled()}
                >
                  {billing ? 'Marking…' : picked.size ? `Mark ${picked.size} as invoiced` : 'Mark as invoiced'}
                </button>
              ) : (
                <span className="dim" style={{ fontSize: 12 }}>
                  What the export contains
                </span>
              )}
            </div>
            <div className="card-pad" style={{ paddingBottom: 0 }}>
              <div className="lane-tabs" role="tablist" aria-label="Which hours" style={{ marginBottom: 8 }}>
                {SHOWS.map((view) => (
                  <button
                    key={view.key}
                    type="button"
                    role="tab"
                    aria-selected={show === view.key}
                    className={show === view.key ? 'lane-tab is-on' : 'lane-tab'}
                    title={view.hint}
                    onClick={() => {
                      setChosenShow(view.key);
                      setPicked(new Set());
                    }}
                  >
                    {view.label}
                    <span className="lane-count">{log.entries.filter((row) => inView(row, view.key)).length}</span>
                  </button>
                ))}
              </div>
              {notice && (
                <div className="callout mb-2" role="status">
                  {notice}
                </div>
              )}
              {log.mayBill && show === 'ready' && (
                <p className="dim" style={{ fontSize: 12, margin: '0 0 8px' }}>
                  Billable, approved and not yet invoiced — tick what went on the invoice, then Mark as invoiced.
                </p>
              )}
            </div>
            <DataTable
              tableKey="time-log-entries"
              rows={shownEntries}
              columns={entryColumns}
              defaultSort={{ key: 'date', direction: 'desc' }}
              searchPlaceholder="Find a person, project or note"
            />
          </div>
        </div>
      )}
    </>
  );
}
